import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  withStyles,
  useTheme,
  useMediaQuery,
  DialogActions,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { PasswordInput } from './TextInput';
import { handleResponse, put, swal500 } from '../../utils/network';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/;

const styles = theme => ({
  dialog: {
    padding: 0,
  },
});

const INITIAL_STATE = {
  currentPassword: '',
  password: '',
  confirmPassword: '',
};

const validate = values => {
  const errors = {};

  if (!values.currentPassword) {
    errors.currentPassword = 'Ingrese su contraseña actual';
  }

  if (!values.password) {
    errors.password = 'Complete la contraseña nueva';
  } else if (values.password.length < 8 || values.password.length > 20) {
    errors.password = 'La contraseña debe tener entre 8 y 20 caracteres';
  } else if (!PASSWORD_REGEX.test(values.password)) {
    errors.password =
      'La contraseña debe tener al menos un caracter en minúscula, uno en mayúscula y un numéro';
  } else if (values.currentPassword && values.password === values.currentPassword) {
    errors.password = 'La nueva contraseña debe ser diferente a la actual';
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Repita la contraseña nueva';
  } else if (values.password && values.confirmPassword !== values.password) {
    errors.confirmPassword = 'Las contraseñas no coinciden';
  }

  return errors;
};

const ChangePasswordDialog = props => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { open, classes, onClose, onSubmit } = props;
  const [values, setValues] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setErrors({ ...errors, [name]: '' });
    setValues({ ...values, [name]: value });
  };

  const resetForm = () => {
    setValues(INITIAL_STATE);
    setErrors({});
  };

  const closeDialog = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    const validationErrors = validate(values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    put('auth/changepassword', { currentPassword: values.currentPassword, password: values.password })
      .then(res =>
        handleResponse(res, props, [
          { status: 401, method: () => Promise.reject(401) },
          { status: 400, method: res => res.json().then(data => Promise.reject({ status: 400, fields: data.fields })) },
        ])
      )
      .then(() => {
        setLoading(false);
        resetForm();
        onClose();
        onSubmit();
      })
      .catch(err => {
        setLoading(false);
        if (err === 401) {
          setErrors({ currentPassword: 'La contraseña actual es incorrecta' });
        } else if (err && err.status === 400 && err.fields) {
          const fieldErrors = {};
          err.fields.forEach(field => {
            fieldErrors[field.name] = field.message;
          });
          setErrors(fieldErrors);
        } else {
          closeDialog();
          swal500(err);
        }
      });
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      className={classes.dialog}
      onClose={closeDialog}
      open={open}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle style={{ padding: '20px 24px 7px' }}>Cambiar contraseña</DialogTitle>
      <DialogContent>
        <PasswordInput
          name="currentPassword"
          label="Contraseña actual"
          value={values.currentPassword}
          error={errors.currentPassword}
          handleChange={handleChange}
        />
        <PasswordInput
          name="password"
          label="Contraseña nueva"
          value={values.password}
          error={errors.password}
          handleChange={handleChange}
          InputProps={{ autoComplete: 'new-password' }}
        />
        <PasswordInput
          name="confirmPassword"
          label="Repetir contraseña nueva"
          value={values.confirmPassword}
          error={errors.confirmPassword}
          handleChange={handleChange}
          InputProps={{ autoComplete: 'new-password' }}
        />
      </DialogContent>
      <DialogActions>
        {loading && <CircularProgress size={22} color="primary" />}
        <Button onClick={closeDialog} disabled={loading} style={{ color: 'gray' }}>
          Cancelar
        </Button>
        <Button color="primary" type="submit" onClick={handleSubmit} disabled={loading}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(ChangePasswordDialog);
